import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { User, UserService } from '../../services/UserService';
import { Doctor, DoctorService } from '../../services/DoctorService';
import { X } from 'lucide-react';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../context/AuthContext';
import AgendaColorField from './AgendaColorField';
import { isAgendaRole, normalizeAgendaColor } from '../../utils/agendaColor';

interface EditUserModalProps {
  user: User;
  onClose: () => void;
  onUpdate: () => void;
}

const EditUserModal = ({ user, onClose, onUpdate }: EditUserModalProps) => {
  const toDateTimeLocalValue = (value?: string | null) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const timezoneOffset = date.getTimezoneOffset() * 60 * 1000;
    return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 16);
  };

  const [isLoading, setIsLoading] = useState(false);
  const [calendars, setCalendars] = useState<string[]>([]);
  const [useCustomCalendar, setUseCustomCalendar] = useState(false);
  const { user: adminUser, selectedClient } = useAuth();
  const activeClientId = selectedClient?.clientId || adminUser?.current_client_id || null;

  const [formData, setFormData] = useState({
    userDisplayName: user.userDisplayName || '',
    userEmail: user.userEmail || '',
    userRole: user.userRole || 'patient',
    crefito: '',
    crm: '',
    userAssignedCalendarName: user.userAssignedCalendarName || '',
    agendaColor: user.agendaColor || '',
    accessStartAt: toDateTimeLocalValue(user.accessStartAt),
    accessEndAt: toDateTimeLocalValue(user.accessEndAt),
  });
  const showCalendarAssignment = formData.userRole === 'fisioterapeuta' || formData.userRole === 'interno';
  const showAgendaColorSettings = isAgendaRole(formData.userRole);

  useEffect(() => {
    const fetchDoctorData = async () => {
      if ((user.userRole === 'doctor' || user.userRole === 'fisioterapeuta') && activeClientId) {
        try {
          const doctorData = await DoctorService.getDoctorByuserId(user.userId);
          if (doctorData) {
            setFormData(prev => ({
              ...prev,
              crefito: doctorData.doctorCrefito || '',
              crm: doctorData.doctorCrm || '',
            }));
          }
        } catch (error) {
          console.error("Error fetching professional data:", error);
          toast.error("Erro ao carregar os dados profissionais.");
        }
      }
    };

    fetchDoctorData();
  }, [activeClientId, user.userId, user.userRole]);

  useEffect(() => {
    const loadCalendars = async () => {
      try {
        if (!activeClientId) {
          setCalendars([]);
          return;
        }

        const { data: usersData, error } = await supabase
          .from('users')
          .select(`
            userAssignedCalendarName,
            user_clients!inner ( clientId )
          `)
          .eq('user_clients.clientId', activeClientId)
          .not('userAssignedCalendarName', 'is', null);

        if (error) {
          throw error;
        }

        const calendarNames = Array.from(
          new Set(
            (usersData || [])
              .map((row: any) => row.userAssignedCalendarName || '')
              .filter((name) => name.trim().length > 0)
          )
        ).sort((a, b) => a.localeCompare(b, 'pt-BR'));

        setCalendars(calendarNames);
      } catch (error) {
        console.error('Error loading calendars:', error);
      }
    };

    if (showCalendarAssignment) {
      loadCalendars();
    } else {
      setCalendars([]);
    }
  }, [activeClientId, showCalendarAssignment]);

  useEffect(() => {
    const currentCalendarName = (formData.userAssignedCalendarName || '').trim();
    if (!currentCalendarName) {
      return;
    }

    if (!calendars.includes(currentCalendarName)) {
      setUseCustomCalendar(true);
    }
  }, [calendars, formData.userAssignedCalendarName]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const normalizedCrefito = formData.crefito.trim();
      if (formData.userRole === 'fisioterapeuta' && !normalizedCrefito) {
        throw new Error('Informe o CREFITO do fisioterapeuta.');
      }

      if (formData.userRole === 'interno') {
        if (!formData.accessStartAt || !formData.accessEndAt) {
          throw new Error('Defina o início e o fim do acesso do perfil Interno.');
        }

        if (new Date(formData.accessEndAt).getTime() <= new Date(formData.accessStartAt).getTime()) {
          throw new Error('O fim do acesso deve ser posterior ao início.');
        }
      }

      // Update user data
      const userData: Partial<User> = {
        userDisplayName: formData.userDisplayName,
        userEmail: formData.userEmail,
        userAssignedCalendarName: formData.userAssignedCalendarName,
        // Não passamos userRole ou current_client_id aqui, pois serão tratados separadamente.
      };

      await UserService.updateUser(user.userId, userData); // Atualiza os dados do usuário, exceto a role

      // Atualizar a role do usuário separadamente, usando o current_client_id do ADMINISTRADOR logado
      if (activeClientId) {
        const clientAssignmentUpdates: {
          userRole: string;
          accessStartAt: string | null;
          accessEndAt: string | null;
          agendaColor?: string | null;
        } = {
          userRole: formData.userRole,
          accessStartAt:
            formData.userRole === 'interno' && formData.accessStartAt
              ? new Date(formData.accessStartAt).toISOString()
              : null,
          accessEndAt:
            formData.userRole === 'interno' && formData.accessEndAt
              ? new Date(formData.accessEndAt).toISOString()
              : null,
        };

        if (showAgendaColorSettings) {
          clientAssignmentUpdates.agendaColor = normalizeAgendaColor(formData.agendaColor);
        }

        await UserService.updateUserClientAssignment(user.userId, activeClientId, {
          ...clientAssignmentUpdates,
        });
      } else {
        console.error("Erro: current_client_id do administrador ausente para atualizar a função do usuário.");
        toast.error("Erro: ID do cliente atual do administrador ausente para atualizar a função do usuário.");
      }

      // Mantém apenas a identidade profissional; regras financeiras são configuradas no Financeiro.
      if (activeClientId && (formData.userRole === 'doctor' || formData.userRole === 'fisioterapeuta')) {
        const doctorUpdates: Partial<Doctor> = formData.userRole === 'fisioterapeuta'
          ? { doctorCrefito: normalizedCrefito }
          : { doctorCrm: formData.crm.trim() };

        let currentDoctorId: string | undefined = undefined;

        // Sempre tentar buscar o Doctor existente pelo userId para ter a informação mais recente
        const existingDoctor = await DoctorService.getDoctorByuserId(user.userId);

        if (existingDoctor) {
          // Se o Doctor já existe, atualiza-o
          await DoctorService.updateDoctor(existingDoctor.doctorId, doctorUpdates);
          currentDoctorId = existingDoctor.doctorId;
        } else {
          // Se não existe, cria um novo Doctor
          const newDoctor = await DoctorService.createDoctor({
            ...doctorUpdates,
            userId: user.userId,
          });
          if (newDoctor) {
            currentDoctorId = newDoctor.doctorId;
          } else {
            console.error("Falha ao criar registro de médico.");
            toast.error("Erro: Falha ao criar registro de médico.");
            setIsLoading(false);
            return; // Interrompe a execução
          }
        }

        if (!currentDoctorId) {
          console.error('Doctor ID ausente para atualizar configurações do cliente do médico.');
          toast.error('Erro: ID do profissional ausente.');
        }
      }

      toast.success("Usuário atualizado com sucesso!");
      onUpdate();
      onClose();
    } catch (error: any) {
      console.error("Erro ao atualizar usuário:", error);
      toast.error(error?.message || "Erro ao atualizar usuário");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Editar Usuário</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Dados pessoais</h4>
          <div>
            <label className="block text-sm font-medium text-gray-700">Nome</label>
            <input
              type="text"
              name="userDisplayName"
              value={formData.userDisplayName}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              type="email"
              name="userEmail"
              value={formData.userEmail}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Perfil nesta clínica</label>
            <select
              name="userRole"
              value={formData.userRole}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="patient">Paciente</option>
              <option value="doctor">Médico</option>
              <option value="admin">Administrador</option>
              <option value="fisioterapeuta">Fisioterapeuta</option>
              <option value="interno">Interno</option>
              <option value="financeiro">Financeiro</option>
            </select>
          </div>

          {(formData.userRole === 'fisioterapeuta' || formData.userRole === 'doctor') && (
            <h4 className="border-t pt-4 text-xs font-semibold uppercase tracking-wide text-gray-500">Dados profissionais</h4>
          )}

          {/* Campo CREFITO para Fisioterapeutas */}
          {formData.userRole === 'fisioterapeuta' && (
            <div>
              <label className="block text-sm font-medium text-gray-700">CREFITO</label>
              <input
                type="text"
                name="crefito"
                value={formData.crefito}
                onChange={handleChange}
                placeholder="Ex: CREFITO-8/123456"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                required
              />
              <p className="mt-1 text-xs text-gray-500">Informe o registro completo; hífen, espaço e barra são aceitos.</p>
            </div>
          )}

          {/* Campo CRM para Médicos */}
          {formData.userRole === 'doctor' && (
            <div>
              <label className="block text-sm font-medium text-gray-700">CRM</label>
              <input
                type="text"
                name="crm"
                value={formData.crm}
                onChange={handleChange}
                placeholder="Ex: CRM-SP/123456"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              />
            </div>
          )}

          {formData.userRole === 'interno' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">Início do acesso</label>
                <input
                  type="datetime-local"
                  name="accessStartAt"
                  value={formData.accessStartAt}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Fim do acesso</label>
                <input
                  type="datetime-local"
                  name="accessEndAt"
                  value={formData.accessEndAt}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
            </>
          )}

          {showAgendaColorSettings && (
            <div className="space-y-4 border-t pt-4 mt-4">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Agenda</h4>
              {showCalendarAssignment && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Calendário Vinculado
                  </label>
                  {!useCustomCalendar ? (
                    <div className="flex gap-2">
                      <select
                        name="userAssignedCalendarName"
                        value={formData.userAssignedCalendarName}
                        onChange={handleChange}
                        className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      >
                        <option value="">Selecione um calendário</option>
                        {calendars.map((cal) => (
                          <option key={cal} value={cal}>
                            {cal}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => setUseCustomCalendar(true)}
                        className="px-3 py-2 text-sm text-primary-600 hover:text-primary-700"
                      >
                        Outro
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="userAssignedCalendarName"
                        value={formData.userAssignedCalendarName}
                        onChange={handleChange}
                        placeholder="Digite o nome do calendário"
                        className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      />
                      <button
                        type="button"
                        onClick={() => setUseCustomCalendar(false)}
                        className="px-3 py-2 text-sm text-gray-600 hover:text-gray-700"
                      >
                        Voltar
                      </button>
                    </div>
                  )}
                </div>
              )}

              <AgendaColorField
                value={formData.agendaColor}
                onChange={(value) => setFormData((prev) => ({ ...prev, agendaColor: value }))}
              />
            </div>
          )}

          {formData.userRole === 'fisioterapeuta' && (
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
              Vínculo, modelo de remuneração, vigência e valores de repasse são administrados em <strong>Financeiro &gt; Configurações</strong>.
            </div>
          )}

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {isLoading ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserModal;
